/**
 * CrisisResources Component
 * 
 * Provides mental health and crisis hotline resources.
 * Should be displayed in footer or accessible from content warnings.
 */

import React from 'react';
import { Heart, Phone, MessageCircle, ExternalLink } from 'lucide-react';

const RESOURCES = [
    {
        name: 'National Sexual Assault Hotline',
        org: 'RAINN',
        phone: '1-800-656-4673',
        url: 'https://www.rainn.org',
        description: '24/7 confidential support'
    },
    {
        name: 'National Domestic Violence Hotline',
        org: 'NDVH',
        phone: '1-800-799-7233',
        url: 'https://www.thehotline.org',
        description: '24/7 support for abuse survivors'
    },
    {
        name: 'Crisis Text Line',
        org: 'Text HOME to 741741',
        phone: null,
        url: 'https://www.crisistextline.org',
        description: 'Free 24/7 text support'
    },
    {
        name: '988 Suicide & Crisis Lifeline',
        org: 'SAMHSA',
        phone: '988',
        url: 'https://988lifeline.org',
        description: 'Mental health crisis support'
    }
];

function CrisisResources({ compact = false }) {
    if (compact) {
        return (
            <div id="safety-resources" className="text-center py-4 border-t border-wine-800/30">
                <p className="text-cream/40 text-sm mb-2">
                    Need support? <span className="text-[#C4A35A]">Crisis resources available 24/7</span>
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-cream/50">
                    <a href="tel:988" className="hover:text-cream/70 transition-colors">
                        988 Crisis Line
                    </a>
                    <span className="text-cream/20">|</span>
                    <a href="https://www.rainn.org" target="_blank" rel="noopener noreferrer" className="hover:text-cream/70 transition-colors">
                        RAINN.org
                    </a>
                    <span className="text-cream/20">|</span>
                    <span>Text HOME to 741741</span>
                </div>
            </div>
        );
    }

    return (
        <section id="safety-resources" className="bg-[#0A0A0A]/80 border border-wine-800/30 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
                <Heart className="w-5 h-5 text-[#C4A35A]" aria-hidden="true" />
                <h3 className="font-fantasy text-lg font-semibold text-cream/90 tracking-wide">
                    Mental Health & Crisis Resources
                </h3>
            </div>

            <p className="text-cream/60 text-sm mb-6">
                Exploring intimacy should always feel safe. If you're experiencing distress, confusion, or need someone to talk to, these resources are available 24/7:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {RESOURCES.map((resource, index) => (
                    <div
                        key={index}
                        className="p-4 rounded-lg bg-wine-950/20 border border-wine-800/20 hover:border-wine-700/30 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-semibold text-cream/90 text-sm">{resource.name}</h4>
                            {resource.url && (
                                <a
                                    href={resource.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#C4A35A] hover:text-[#C4A35A]/80"
                                    aria-label={`Visit ${resource.org} website`}
                                >
                                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                                </a>
                            )}
                        </div>

                        <p className="text-cream/50 text-xs mb-2">{resource.description}</p>

                        {resource.phone ? (
                            <a
                                href={`tel:${resource.phone.replace(/-/g, '')}`}
                                className="inline-flex items-center gap-2 text-sm text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors"
                            >
                                <Phone className="w-3 h-3" aria-hidden="true" />
                                {resource.phone}
                            </a>
                        ) : (
                            <span className="inline-flex items-center gap-2 text-sm text-cream/60">
                                <MessageCircle className="w-3 h-3" aria-hidden="true" />
                                {resource.org}
                            </span>
                        )}
                    </div>
                ))}
            </div>

            <p className="text-cream/40 text-xs mt-4 text-center">
                Your wellbeing matters. These services are free and confidential.
            </p>
        </section>
    );
}

export default CrisisResources;
